import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { DefinitionTooltip } from "src/components/common";
import type { PlatformReportRow } from "../../data/paidMediaPlatformReportMockData";
import { OVERVIEW_GRID_COLOR, overviewAxisProps } from "../overview/overviewChartConfig";
import { OverviewChartEmptyState } from "../overview/overviewPrimitives";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";
import {
  platformReportSectionHelp,
  PLATFORM_REPORT_CHART_ENTRIES,
  PLATFORM_REPORT_METRIC_COLORS,
} from "./platformReportConfig";
import { formatPlatformReportAxis, formatPlatformReportValue } from "./platformReportFormat";

interface PlatformReportChartsRowProps {
  campaigns: PlatformReportRow[];
}

const truncate = (value: string, limit = 18): string =>
  value.length > limit ? `${value.slice(0, limit - 1)}…` : value;

const PlatformReportChartsRow: FC<PlatformReportChartsRowProps> = ({ campaigns }) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const topBySpend = [...campaigns]
    .sort((left, right) => right.spend - left.spend)
    .slice(0, PLATFORM_REPORT_CHART_ENTRIES)
    .map(row => ({ name: truncate(row.name), value: row.spend }));

  const topByRoas = [...campaigns]
    .sort((left, right) => (right.roas ?? 0) - (left.roas ?? 0))
    .slice(0, PLATFORM_REPORT_CHART_ENTRIES)
    .map(row => ({ name: truncate(row.name), value: row.roas }));

  const renderChart = (
    rows: Array<{ name: string; value: number | null }>,
    fill: string,
    format: "money" | "multiplier"
  ) => {
    if (!rows.length) {
      return <OverviewChartEmptyState message={t("platformAdsReport.empty")} />;
    }

    return (
      <ResponsiveContainer>
        <BarChart
          layout="vertical"
          data={rows}
          margin={chartRtl.margin({ top: 8, right: 18, bottom: 5, left: 8 })}
        >
          <CartesianGrid stroke={OVERVIEW_GRID_COLOR} horizontal={false} />
          <XAxis
            type="number"
            reversed={chartRtl.axisReversed}
            tickFormatter={value => formatPlatformReportAxis(value, format)}
            {...overviewAxisProps}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={128}
            orientation={chartRtl.yAxisOrientation}
            {...overviewAxisProps}
          />
          <Tooltip
            wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
            content={
              <CustomChartTooltip
                formatTooltip={(value: number) => formatPlatformReportValue(value, format)}
              />
            }
          />
          <Bar dataKey="value" name={t("platformAdsReport.kpis.spend")} fill={fill} radius={[3, 3, 3, 3]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="platform-report__two-col">
      <article className="paid-media-panel">
        <div className="paid-media-panel__header">
          <div className="paid-media-panel__heading">
            <h3>{t("platformAdsReport.sections.spendByCampaign")}</h3>
            <DefinitionTooltip
              label={t("platformAdsReport.sections.spendByCampaign")}
              info={platformReportSectionHelp(t, "spendDistributionByCampaign")}
            />
          </div>
        </div>
        <div className="paid-media-chart paid-media-chart--compact">
          {renderChart(topBySpend, PLATFORM_REPORT_METRIC_COLORS.spend, "money")}
        </div>
      </article>

      <article className="paid-media-panel">
        <div className="paid-media-panel__header">
          <div className="paid-media-panel__heading">
            <h3>{t("platformAdsReport.sections.roasByCampaign")}</h3>
            <DefinitionTooltip
              label={t("platformAdsReport.sections.roasByCampaign")}
              info={platformReportSectionHelp(t, "roasByCampaign")}
            />
          </div>
        </div>
        <div className="paid-media-chart paid-media-chart--compact">
          {renderChart(topByRoas, PLATFORM_REPORT_METRIC_COLORS.conversions, "multiplier")}
        </div>
      </article>
    </div>
  );
};

export default PlatformReportChartsRow;
